import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { FeedSignal, SignalType, VerificationStatus } from "../types";
import { useClaimSignal, useToggleSave, useUnclaimSignal } from "../hooks";
import { abbreviateName, archiveReasonLabel, shortDate } from "../format";

/**
 * Redesign · feed card (sealed mock). Card anatomy is the addendum's (ruled in
 * at the gate): score + tier with an in-card breakdown tooltip, eyebrow,
 * headline, three pills (category / confidence / region), the metadata row, and
 * the ownership column. No type icon, no practice tag.
 *
 * Wired to the live feed projection exactly as it is. Where the sealed mock
 * renders a field the feed does not carry, the honest data governs and the
 * conflict is flagged in the session report:
 *   - "Latest ledger action" (.c-action): FeedSignal carries no such field, so
 *     the line is omitted rather than faked. (Action history lives behind
 *     get_signal_action_history, not on the feed row.)
 *   - Emerging badge: no backing field; not rendered (ships with the Precursor
 *     slice).
 *   - Two-company eyebrow: the feed carries a single `company`; rendered
 *     verbatim, not split.
 *   - "Low confidence": the sealed pill set has only high/medium materials, so a
 *     low-confidence signal takes the medium material with an honest "LOW
 *     CONFIDENCE" label.
 *   - Assigned / archived states are not depicted in the mock; mapped onto its
 *     chip materials (mine/other) honestly.
 *
 * The whole card opens the detail modal at /signals/:id; the inline controls
 * (score, star, claim, unclaim) stop propagation so they never double as a
 * navigation.
 */

const CATEGORY_PILL: Record<SignalType, { cls: string; label: string }> = {
  rfp: { cls: "sx-cat-rfp", label: "RFPS / TENDERS" },
  hiring: { cls: "sx-cat-hiring", label: "HIRING" },
  m_and_a: { cls: "sx-cat-ma", label: "M&A / TRANSACTIONS" },
  transformation: { cls: "sx-cat-trans", label: "TRANSFORMATION" },
  leadership: { cls: "sx-cat-lead", label: "LEADERSHIP" },
  market_intel: { cls: "sx-cat-intel", label: "MARKET INTEL" },
  target_account: { cls: "sx-cat-target", label: "TARGET ACCTS" },
};

const VERIF_EYEBROW: Record<VerificationStatus, string> = {
  verified: "Verified",
  quarantined: "Quarantined",
  unverified: "Unverified",
};

/** confidence value (0–1 or 0–100) → the sealed pill (only high/medium exist). */
function confidencePill(
  n: number,
): { cls: string; label: string } {
  const v = n <= 1 ? n * 100 : n;
  if (v >= 75) return { cls: "sx-conf-high", label: "HIGH CONFIDENCE" };
  if (v >= 50) return { cls: "sx-conf-medium", label: "MEDIUM CONFIDENCE" };
  // No sealed "low" material; take the medium material with an honest label.
  return { cls: "sx-conf-medium", label: "LOW CONFIDENCE" };
}

const STAR_PATH =
  "M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9z";

export function SignalCard({
  signal,
  viewerId,
}: {
  signal: FeedSignal;
  viewerId: string;
}): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const claim = useClaimSignal();
  const unclaim = useUnclaimSignal();
  const toggleSave = useToggleSave();

  // Preserve the active tab/type query so the feed underneath the modal stays put.
  const open = () => navigate(`/signals/${signal.id}${location.search}`);

  const cat = CATEGORY_PILL[signal.signalType];
  const conf = signal.confidence != null ? confidencePill(signal.confidence) : null;
  const region = signal.geography;
  const bd = signal.scoreBreakdown;

  const meta = [
    signal.source,
    signal.eventType,
    signal.deadlineAt ? `Deadline ${shortDate(signal.deadlineAt)}` : null,
    signal.lastObservedAt ? `Updated ${shortDate(signal.lastObservedAt)}` : null,
    signal.firstObservedAt
      ? `First seen ${shortDate(signal.firstObservedAt)}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="sx-card"
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      aria-label={`Open signal for ${signal.company}`}
    >
      <div className="sx-card-row">
        <button
          type="button"
          className="sx-c-score"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Score ${signal.scfScore} of 100, Tier ${signal.tier}. Show breakdown.`}
        >
          <div className="sx-c-val">
            {signal.scfScore}
            <span className="sx-c-den">/100</span>
          </div>
          <div className="sx-c-tier">TIER {signal.tier}</div>
          <div className="sx-score-tip" role="tooltip">
            <p className="sx-st-head">Score breakdown</p>
            <dl>
              <dt>Signal weight</dt>
              <dd>{bd.signalWeight}</dd>
              <dt>Capability fit</dt>
              <dd>{bd.capabilityFit}</dd>
              <dt>Recency</dt>
              <dd>{bd.recency}</dd>
              <dt>Target account bonus</dt>
              <dd>{bd.targetAccountBonus > 0 ? `+${bd.targetAccountBonus}` : "0"}</dd>
              <dt className="sx-st-total">Score</dt>
              <dd className="sx-st-total">{signal.scfScore}</dd>
            </dl>
          </div>
        </button>

        <div className="sx-c-main">
          <p className="sx-c-eyebrow">
            {signal.company} · {VERIF_EYEBROW[signal.verification]}
          </p>
          <h2 className="sx-c-head">{signal.headline}</h2>
          <div className="sx-c-pills">
            <span className={`sx-cpill ${cat.cls}`}>{cat.label}</span>
            {conf ? (
              <span className={`sx-cpill sx-conf ${conf.cls}`}>{conf.label}</span>
            ) : null}
            {region ? (
              <span className="sx-cpill sx-region">{region.toUpperCase()}</span>
            ) : null}
          </div>
          <p className="sx-c-meta">{meta}</p>
        </div>

        <div className="sx-c-right">
          <button
            type="button"
            className={`sx-star${signal.saved ? " sx-star--on" : ""}`}
            aria-pressed={signal.saved}
            aria-label="Save signal"
            disabled={toggleSave.isPending}
            onClick={(e) => {
              e.stopPropagation();
              toggleSave.mutate(signal.id);
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={STAR_PATH} />
            </svg>
          </button>

          <CardOwnership
            signal={signal}
            viewerId={viewerId}
            claiming={claim.isPending}
            claimError={claim.isError ? claimErrorMessage(claim.error) : null}
            unclaiming={unclaim.isPending}
            onClaim={(e) => {
              e.stopPropagation();
              claim.reset();
              claim.mutate(signal.id);
            }}
            onUnclaim={(e) => {
              e.stopPropagation();
              unclaim.mutate(signal.id);
            }}
          />
        </div>
      </div>
    </div>
  );
}

const CHECK_ICON = (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/**
 * Ownership column (sealed mock, three depicted states plus the honest
 * assigned/archived mappings). The follow-up date renders only on the viewer's
 * own signal — a follow-up is personal and never hangs off a colleague's card.
 */
function CardOwnership({
  signal,
  viewerId,
  claiming,
  claimError,
  unclaiming,
  onClaim,
  onUnclaim,
}: {
  signal: FeedSignal;
  viewerId: string;
  claiming: boolean;
  claimError: string | null;
  unclaiming: boolean;
  onClaim: (e: React.MouseEvent) => void;
  onUnclaim: (e: React.MouseEvent) => void;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);

  const isViewers =
    (signal.ownershipState === "claimed" && signal.claimedBy === viewerId) ||
    (signal.ownershipState === "assigned" && signal.assignedTo === viewerId);
  const followUp =
    isViewers && signal.followUpDueAt ? (
      <p className="sx-fu-mono">
        FOLLOW-UP {shortDate(signal.followUpDueAt).toUpperCase()}
      </p>
    ) : null;

  // Archived (not in the mock) — muted chip, no ownership actions.
  if (signal.lifecycleStatus === "archived") {
    const label = signal.archiveReason
      ? `Archived · ${archiveReasonLabel(signal.archiveReason)}`
      : "Archived";
    return (
      <span className="sx-ownchip sx-ownchip--other">● {label}</span>
    );
  }

  if (signal.ownershipState === "unclaimed") {
    return (
      <>
        <p className="sx-own-eyebrow">● Unclaimed</p>
        <button
          type="button"
          className="sx-dbtn sx-dhero"
          onClick={onClaim}
          disabled={claiming}
        >
          {claiming ? "Claiming…" : "Claim signal"}
        </button>
        {claimError ? (
          <p className="sx-action-error" role="status">
            {claimError}
          </p>
        ) : null}
      </>
    );
  }

  if (signal.ownershipState === "claimed" && signal.claimedBy === viewerId) {
    return (
      <>
        <span className="sx-ownchip sx-ownchip--mine">
          {CHECK_ICON}
          Claimed · {signal.claimedByName ? abbreviateName(signal.claimedByName) : "You"}
        </span>
        {confirming ? (
          <span className="sx-confirm-row">
            <button
              type="button"
              className="sx-dbtn sx-dter"
              disabled={unclaiming}
              onClick={onUnclaim}
            >
              {unclaiming ? "Releasing…" : "Unclaim"}
            </button>
            <button
              type="button"
              className="sx-dbtn sx-dter"
              onClick={(e) => {
                e.stopPropagation();
                setConfirming(false);
              }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="sx-dbtn sx-dter"
            onClick={(e) => {
              e.stopPropagation();
              setConfirming(true);
            }}
          >
            Unclaim
          </button>
        )}
        {followUp}
      </>
    );
  }

  if (signal.ownershipState === "claimed") {
    return (
      <span className="sx-ownchip sx-ownchip--other">
        ● Claimed · {abbreviateName(signal.claimedByName)}
      </span>
    );
  }

  // assigned
  if (signal.assignedTo === viewerId) {
    return (
      <>
        <span className="sx-ownchip sx-ownchip--mine">● Assigned to you</span>
        {followUp}
      </>
    );
  }
  return (
    <span className="sx-ownchip sx-ownchip--other">
      ● Assigned · {abbreviateName(signal.assignedToName)}
    </span>
  );
}

function claimErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/claim|assigned|ownership|forbidden|403|cannot/i.test(message)) {
    return "Claim did not complete — it may already be owned.";
  }
  return "Claim did not complete. Try again.";
}
